/**
 * Stable file URLs, and repair of the expiring ones saved before them.
 *
 * Uploads now return an absolute, signed, non-expiring URL
 * (`{api}/api/files/serve/{key}?sig=...` — see backend
 * services/uploads/stable_urls.py), so nothing saved from here on rots.
 *
 * Content saved BEFORE that has a presigned S3 URL baked into it, with at most
 * 7 days of life from the moment it was written. Most of it is already dead.
 * Those URLs can't be fixed server-side — the browser requests them straight
 * from S3, so this API never sees the request — but the object key is right
 * there in the URL's path, and the key is all the backend needs to mint the
 * permanent replacement.
 */

import authService from './auth/authService';
import { API_BASE_URL } from '../api/config';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('STABLE_FILE_URL');

/** Presigned URLs are stamped with SigV4 query params; stable ones never are. */
export function isExpiringFileUrl(url: string): boolean {
  return url.includes('X-Amz-Signature=') || url.includes('X-Amz-Credential=');
}

/**
 * Pull the S3 object key out of a storage URL, or null if it isn't one.
 *
 * Handles every layout the bucket has been addressed by — virtual-hosted
 * (`bucket.s3.region.amazonaws.com/users/...`), path-style
 * (`s3.region.amazonaws.com/bucket/users/...`), and MinIO
 * (`localhost:9000/bucket/users/...`) — by anchoring on the `users/` segment
 * that begins every key rather than on the bucket, which differs per
 * environment.
 */
export function extractS3ObjectKey(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const match = parsed.pathname.match(/\/(users\/.+)$/);
  if (!match) return null;

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * One in-flight request per key, and one cached answer per key.
 *
 * A document repeats the same image, and a re-render re-resolves it; without
 * this a gallery of twenty copies would fire twenty signing requests.
 */
const resolvedUrls = new Map<string, string>();
const inFlight = new Map<string, Promise<string | null>>();

async function requestStableUrl(objectKey: string): Promise<string | null> {
  const token = authService.getAccessToken();
  if (!token) return null;

  const response = await fetch(
    `${API_BASE_URL}/files/stable-url?path=${encodeURIComponent(objectKey)}`,
    { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' },
  );

  if (!response.ok) {
    // A 403 here is normal and permanent: the file belongs to someone else, or
    // predates the current user's ownership. Caching the null below stops us
    // asking again for the rest of the session.
    log.warn('[stableFileUrl] could not sign object key', { objectKey, status: response.status });
    return null;
  }

  const data = await response.json();
  return typeof data?.url === 'string' ? data.url : null;
}

/**
 * Return the permanent URL for a legacy storage URL, or null when it can't be
 * repaired (not one of ours, not signable, offline).
 */
export async function resolveStableFileUrl(url: string): Promise<string | null> {
  const objectKey = extractS3ObjectKey(url);
  if (!objectKey) return null;

  const cached = resolvedUrls.get(objectKey);
  if (cached) return cached;

  let pending = inFlight.get(objectKey);
  if (!pending) {
    pending = requestStableUrl(objectKey).finally(() => inFlight.delete(objectKey));
    inFlight.set(objectKey, pending);
  }

  const stable = await pending;
  if (stable) resolvedUrls.set(objectKey, stable);
  return stable;
}

/** Marks an <img> we've already tried, so a second failure doesn't loop. */
const REPAIR_ATTEMPTED_ATTR = 'data-stable-url-repaired';

/**
 * Swap an expired URL on a broken <img> for its permanent equivalent.
 *
 * Returns whether a repair was applied. Only the on-screen element is fixed —
 * the saved document still holds the old URL and will be repaired again on the
 * next open (from cache, so at most one signing request per key per session).
 * Rewriting the document itself would mean mutating Y.Doc content out from
 * under whoever else has it open, for no visible gain.
 */
export async function repairBrokenImage(img: HTMLImageElement): Promise<boolean> {
  if (img.hasAttribute(REPAIR_ATTEMPTED_ATTR)) return false;
  const src = img.getAttribute('src') || '';
  if (!src || !isExpiringFileUrl(src)) return false;

  img.setAttribute(REPAIR_ATTEMPTED_ATTR, 'true');

  const stable = await resolveStableFileUrl(src);
  if (!stable) return false;

  log.info('[stableFileUrl] repaired an expired image URL', { src: src.split('?')[0] });
  img.src = stable;
  return true;
}

let listenerInstalled = false;

/**
 * Install the app-wide repair hook. Idempotent; call once at startup.
 *
 * A single capture-phase listener rather than a hook in each of the dozen
 * places that render an uploaded image (both editors' node views, Canvas,
 * Presentations, Page, publish previews…). Load failures don't bubble, but
 * they do propagate through the capture phase, so this sees all of them, and
 * the guards in `repairBrokenImage` keep it inert for every failure that isn't
 * one of our expired storage URLs.
 */
export function installLegacyImageUrlRepair(): void {
  if (listenerInstalled || typeof document === 'undefined') return;
  listenerInstalled = true;

  document.addEventListener(
    'error',
    (event) => {
      const target = event.target;
      if (target instanceof HTMLImageElement) {
        void repairBrokenImage(target);
      }
    },
    true,
  );
}

/** Test seam — drops memoised signatures between cases. */
export function __resetStableFileUrlCache(): void {
  resolvedUrls.clear();
  inFlight.clear();
}
